import { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { FileText, Search, Users, X } from 'lucide-react';
import { posApi } from '@/services/api';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import GroupByToggle from '@/components/reports/GroupByToggle';
import Pagination, { LIST_PAGE_SIZE } from '@/components/common/Pagination';
import ReportExportButton from '@/components/reports/ReportExportButton';
import { downloadCsv } from '@/utils/csv';
import { formatMoney } from '@/utils/currency';

interface SaleRegisterCustomer {
  name: string;
  customer_name: string;
  mobile?: string;
}

export default function SaleRegisterSection() {
  const { t } = useTranslation();
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedCustomerLabel, setSelectedCustomerLabel] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const customerDropdownRef = useRef<HTMLDivElement>(null);
  const [groupBy, setGroupBy] = useState<'Invoice' | 'Item'>('Item');
  const [page, setPage] = useState(0);

  const { data: customersRes } = useQuery({
    queryKey: ['customersForSaleRegister', customerSearch],
    queryFn: async () => {
      const res = await posApi.getCustomers({ search: customerSearch, limit: 50 });
      return res.data.message;
    },
  });

  const customers: SaleRegisterCustomer[] = customersRes?.customers || customersRes || [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target as Node)) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectCustomer = (customer?: SaleRegisterCustomer) => {
    if (!customer) {
      setSelectedCustomer('');
      setSelectedCustomerLabel('');
    } else {
      setSelectedCustomer(customer.name);
      setSelectedCustomerLabel(
        customer.mobile ? `${customer.customer_name} - ${customer.mobile}` : customer.customer_name
      );
    }
    setCustomerSearch('');
    setShowCustomerDropdown(false);
  };

  const { data: registerData, isLoading } = useQuery({
    queryKey: ['saleRegister', from_date, to_date, selectedCustomer, groupBy],
    queryFn: async () => {
      const res = await posApi.getCustomerLedger({
        from_date,
        to_date,
        customer: selectedCustomer || undefined,
        group_by: groupBy,
      });
      return res.data.message || [];
    },
  });

  const summaryMetrics = useMemo(() => {
    if (!registerData || registerData.length === 0) return { totalSales: 0, itemsSold: 0, transactions: 0 };
    
    let totalSales = 0;
    let itemsSold = 0;
    const transactions = registerData.length;

    registerData.forEach((row: any) => {
      if (groupBy === 'Invoice') {
        totalSales += Number(row.net_total || 0);
      } else {
        totalSales += Number(row.amount || 0);
        itemsSold += Number(row.qty || 0);
      }
    });

    return { totalSales, itemsSold, transactions };
  }, [registerData, groupBy]);

  useEffect(() => {
    setPage(0);
  }, [registerData, from_date, to_date, selectedCustomer, groupBy]);

  const handleExport = () => {
    if (!registerData || registerData.length === 0) return;
    
    if (groupBy === 'Invoice') {
      const data = registerData.map((row: any) => {
        const baseAmount = (row.item_total || 0) + (row.service_total || 0);
        const discountAmt = row.discount_amount || 0;
        const discountPct = baseAmount > 0 ? (discountAmt / baseAmount) * 100 : 0;
        const taxAmt = row.tax_amount || 0;
        const taxPct = (baseAmount - discountAmt) > 0 ? (taxAmt / (baseAmount - discountAmt)) * 100 : 0;
        
        return {
          date: row.creation ? format(new Date(row.creation), 'd MMM yyyy, hh:mm a') : '',
          name: row.name,
          customer_name: row.customer_name,
          status: row.status,
          discount_pct: discountPct.toFixed(1),
          discount_amt: discountAmt,
          tax_pct: taxPct.toFixed(1),
          tax_amt: taxAmt,
          net_total: row.net_total,
          paid: row.paid_amount,
          balance: row.balance_amount
        };
      });
      downloadCsv(
        `Sale_Register_Invoice_${format(new Date(), 'yyyy-MM-dd')}.csv`,
        [
          { key: 'date', label: 'Date' },
          { key: 'name', label: 'Invoice ID' },
          { key: 'customer_name', label: 'Customer' },
          { key: 'status', label: 'Status' },
          { key: 'discount_pct', label: 'Discount %' },
          { key: 'discount_amt', label: 'Discount Amount' },
          { key: 'tax_pct', label: 'Tax %' },
          { key: 'tax_amt', label: 'Tax Amount' },
          { key: 'net_total', label: 'Net Total' },
          { key: 'paid', label: 'Paid' },
          { key: 'balance', label: 'Balance' }
        ],
        data
      );
    } else {
      const data = registerData.map((row: any) => ({
        date: row.creation
                  ? format(new Date(row.creation), 'd MMM yyyy, hh:mm a')
                  : row.invoice_date
                    ? format(new Date(row.invoice_date), 'd MMM yyyy')
                    : '',
        item: `${row.item_name}${row.variant_name ? ` - ${row.variant_name}` : ''}`,
        qty: row.quantity,
        rate: row.rate,
        amount: row.amount
      }));
      downloadCsv(
        `Sale_Register_Item_${format(new Date(), 'yyyy-MM-dd')}.csv`,
        [
          { key: 'date', label: 'Date' },
          { key: 'item', label: 'Item' },
          { key: 'qty', label: 'Qty' },
          { key: 'rate', label: 'Rate' },
          { key: 'amount', label: 'Amount' }
        ],
        data
      );
    }
  };

  return (
    <div className="space-y-6">


      <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/60 border border-slate-100 overflow-hidden">
        <div className="p-5 border-b border-slate-100 space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          <ReportDateRangeFilter
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            customFrom={customFrom}
            customTo={customTo}
            onCustomFromChange={setCustomFrom}
            onCustomToChange={setCustomTo}
          />

          <div className="space-y-1.5 flex-1 min-w-[200px] sm:min-w-[240px] relative" ref={customerDropdownRef}>
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" />
              Customer
            </label>
            {selectedCustomer ? (
              <div className="input-field flex items-center justify-between gap-2 py-2">
                <span className="truncate text-sm text-gray-900">{selectedCustomerLabel}</span>
                <button
                  type="button"
                  onClick={() => handleSelectCustomer()}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  aria-label="Clear customer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    className="input-field pl-9"
                    placeholder={t('reports.searchByNamePhone')}
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      setShowCustomerDropdown(true);
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                  />
                </div>
                {showCustomerDropdown && (
                  <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto">
                    <button
                      type="button"
                      onClick={() => handleSelectCustomer()}
                      className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 border-b border-gray-100 text-gray-700"
                    >
                      All Customers
                    </button>
                    {customers.length > 0 ? (
                      customers.map((c) => (
                        <button
                          key={c.name}
                          type="button"
                          onClick={() => handleSelectCustomer(c)}
                          className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50"
                        >
                          <p className="font-medium text-gray-900">{c.customer_name}</p>
                          {c.mobile && <p className="text-xs text-gray-500">{c.mobile}</p>}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-4 text-sm text-gray-400 text-center">{t('reports.noCustomersFound')}</div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <GroupByToggle groupBy={groupBy} onChange={setGroupBy} />
          <ReportExportButton onClick={handleExport} disabled={isLoading || !registerData?.length} />
        </div>
      </div>

      <div className="overflow-x-auto max-h-[420px]">
        {isLoading ? (
          <div className="p-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : registerData && registerData.length > 0 ? (
          <table className="w-full text-sm text-left min-w-[520px]">
            <thead className="text-xs font-bold text-[#1fa8ff] bg-white uppercase sticky top-0 border-b border-gray-100">
              {groupBy === 'Invoice' ? (
                <tr>
                  <th className="px-4 py-3 font-medium">{t('common.date')}</th>
                  <th className="px-4 py-3 font-medium">{t('reports.invoiceId')}</th>
                  <th className="px-4 py-3 font-medium">{t('reports.customer')}</th>
                  <th className="px-4 py-3 font-medium">{t('common.status')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('common.discount')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('common.tax')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('reports.netTotal')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('reports.paid')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('reports.balance')}</th>
                </tr>
              ) : (
                <tr>
                  <th className="px-4 py-3 font-medium">{t('common.date')}</th>
                  <th className="px-4 py-3 font-medium">{t('reports.item')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('reports.qty')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('common.rate')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('common.amount')}</th>
                </tr>
              )}
            </thead>
            <tbody className="divide-y divide-gray-100">
              {registerData.slice(page * LIST_PAGE_SIZE, (page + 1) * LIST_PAGE_SIZE).map((row: any, i: number) => (
                <tr key={i} className="hover:bg-slate-50 transition-colors">
                  {groupBy === 'Invoice' ? (
                    <>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                        {row.creation ? format(new Date(row.creation), 'd MMM yyyy, hh:mm a') : ''}
                      </td>
                      <td className="px-4 py-3 font-medium text-primary-600">{row.name}</td>
                      <td className="px-4 py-3 text-gray-900">{row.customer_name}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">
                          {row.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div>{formatMoney(Number(row.discount_amount || 0))}</div>
                        <div className="text-xs text-gray-400">
                          {((row.item_total || 0) + (row.service_total || 0)) > 0 
                            ? ((row.discount_amount || 0) / ((row.item_total || 0) + (row.service_total || 0)) * 100).toFixed(1)
                            : '0'}%
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div>{formatMoney(Number(row.tax_amount || 0))}</div>
                        <div className="text-xs text-gray-400">
                          {(((row.item_total || 0) + (row.service_total || 0)) - (row.discount_amount || 0)) > 0 
                            ? ((row.tax_amount || 0) / (((row.item_total || 0) + (row.service_total || 0)) - (row.discount_amount || 0)) * 100).toFixed(1)
                            : '0'}%
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-gray-900">
                        {formatMoney(Number(row.net_total || 0))}
                      </td>
                      <td className="px-4 py-3 text-right text-emerald-600">
                        {formatMoney(Number(row.paid_amount || 0))}
                      </td>
                      <td className="px-4 py-3 text-right text-amber-600">
                        {formatMoney(Number(row.balance_amount || 0))}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                        {row.creation
                          ? format(new Date(row.creation), 'd MMM yyyy, hh:mm a')
                          : row.invoice_date
                            ? format(new Date(row.invoice_date), 'd MMM yyyy')
                            : ''}
                      </td>
                      <td className="px-4 py-3 text-gray-900">
                        {row.item_name}
                        {row.variant_name ? ` — ${row.variant_name}` : ''}
                      </td>
                      <td className="px-4 py-3 text-right">{row.quantity}</td>
                      <td className="px-4 py-3 text-right text-gray-500">
                        {formatMoney(Number(row.rate || 0))}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-gray-900">
                        {formatMoney(Number(row.amount || 0))}
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {registerData && registerData.length > 0 && (
                <tr className="bg-white border-t border-gray-100 font-medium">
                  {groupBy === 'Invoice' ? (
                    <>
                      <td colSpan={6} className="px-4 py-4 text-right text-gray-500">{t('reports.grandTotal')}</td>
                      <td className="px-4 py-4 text-right font-bold text-[#1fa8ff]">
                        {formatMoney(summaryMetrics.totalSales)}
                      </td>
                      <td colSpan={2}></td>
                    </>
                  ) : (
                    <>
                      <td colSpan={2} className="px-4 py-4 text-right text-gray-500">{t('reports.grandTotal')}</td>
                      <td className="px-4 py-4 text-right font-medium text-gray-900">
                        {summaryMetrics.itemsSold.toLocaleString()}
                      </td>
                      <td></td>
                      <td className="px-4 py-4 text-right font-bold text-[#1fa8ff]">
                        {formatMoney(summaryMetrics.totalSales)}
                      </td>
                    </>
                  )}
                </tr>
              )}
            </tbody>
          </table>
        ) : (
          <div className="p-12 text-center">
            <FileText className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">{t('reports.noSales')}</p>
          </div>
        )}
      </div>

      {registerData && registerData.length > 0 && (
        <div className="p-4 border-t border-gray-100 bg-gray-50">
          <Pagination page={page} pageSize={LIST_PAGE_SIZE} total={registerData.length} onPageChange={setPage} />
        </div>
      )}
      </div>
    </div>
  );
}
